import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FaChevronRight,
  FaHome,
  FaRoute,
  FaStore,
  FaTimes,
  FaTruck,
} from "react-icons/fa";
import {
  buildOrderTrackingJourney,
  JourneyConnectorVisual,
  JourneyNode,
  JourneyNodeVisual,
  OrderTrackingOrderLike,
  OrderTrackingShipmentLike,
} from "./buildOrderTrackingJourney";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  order: OrderTrackingOrderLike;
  shipment?: OrderTrackingShipmentLike | null;
  isLoading?: boolean;
};

/** Fixed circle size so connectors always meet icon centers. */
const NODE_SIZE = 48;
const NODE_CENTER = NODE_SIZE / 2;

const nodeColors = (visual: JourneyNodeVisual) => {
  switch (visual) {
    case "green":
      return {
        circleBg: "#38A169",
        circleBorder: "#38A169",
        iconColor: "white",
        labelColor: "green.700",
        nameColor: "gray.800",
        statusColor: "gray.800",
      };
    case "blue":
      return {
        circleBg: "#3182CE",
        circleBorder: "#3182CE",
        iconColor: "white",
        labelColor: "blue.600",
        nameColor: "gray.800",
        statusColor: "blue.600",
      };
    case "neutral":
      return {
        circleBg: "#EDF2F7",
        circleBorder: "#A0AEC0",
        iconColor: "#4A5568",
        labelColor: "gray.600",
        nameColor: "gray.700",
        statusColor: "gray.600",
      };
    case "grey":
    default:
      return {
        circleBg: "#F7FAFC",
        circleBorder: "#CBD5E0",
        iconColor: "#A0AEC0",
        labelColor: "gray.500",
        nameColor: "gray.600",
        statusColor: "gray.500",
      };
  }
};

const connectorColor = (visual: JourneyConnectorVisual) => {
  if (visual === "green") return "#48BB78";
  if (visual === "blue") return "#4299E1";
  return "#CBD5E0";
};

const badgeScheme = (badge: string) => {
  if (badge === "IN TRANSIT" || badge === "DISPATCHED") return "blue";
  if (badge === "DELIVERED") return "green";
  if (badge.startsWith("AT ")) return "purple";
  return "gray";
};

function nodeIcon(node: JourneyNode) {
  if (node.statusLabel === "In Transit") return FaTruck;
  if (node.type === "CUSTOMER") return FaHome;
  return FaStore;
}

function JourneyNodeColumn({
  node,
  connector,
  isLast,
}: {
  node: JourneyNode;
  connector?: JourneyConnectorVisual;
  isLast: boolean;
}) {
  const colors = nodeColors(node.visual);
  const NodeIcon = nodeIcon(node);

  return (
    <Box flex="1" minW="140px" position="relative" px={1}>
      {/* Connector centered on the icon circle midline */}
      {!isLast && connector && (
        <Box
          position="absolute"
          top={`${NODE_CENTER}px`}
          left="50%"
          width="100%"
          height="0"
          borderTopWidth="3px"
          borderTopStyle={connector === "grey" ? "dashed" : "solid"}
          borderTopColor={connectorColor(connector)}
          transform="translateY(-50%)"
          zIndex={0}
          pointerEvents="none"
        />
      )}

      <VStack spacing={2} align="center" position="relative" zIndex={1}>
        <Flex
          w={`${NODE_SIZE}px`}
          h={`${NODE_SIZE}px`}
          minW={`${NODE_SIZE}px`}
          minH={`${NODE_SIZE}px`}
          borderRadius="full"
          bg={colors.circleBg}
          borderWidth="2px"
          borderColor={colors.circleBorder}
          align="center"
          justify="center"
          flexShrink={0}
          boxShadow={node.visual === "blue" ? "md" : "none"}
        >
          <Icon
            as={NodeIcon}
            color={colors.iconColor}
            boxSize="18px"
            display="block"
          />
        </Flex>

        <VStack spacing={0.5} align="center" w="100%" px={1}>
          <HStack spacing={1} justify="center" maxW="100%">
            <Text
              fontSize="sm"
              fontWeight="bold"
              color={colors.nameColor}
              textAlign="center"
              noOfLines={2}
              lineHeight="1.25"
            >
              {node.name}
            </Text>
            {!isLast && (
              <Icon
                as={FaChevronRight}
                boxSize="10px"
                color="gray.400"
                flexShrink={0}
                display={{ base: "none", md: "block" }}
              />
            )}
          </HStack>
          {node.cityState && (
            <Text
              fontSize="xs"
              color="gray.500"
              textAlign="center"
              noOfLines={1}
            >
              {node.cityState}
            </Text>
          )}
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={colors.statusColor}
            textTransform="capitalize"
            textAlign="center"
            mt={1}
          >
            {node.statusLabel}
          </Text>
          <Text fontSize="xs" color="gray.500" textAlign="center">
            {node.timestamp || "—"}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
}

function HorizontalJourney({
  nodes,
  connectors,
}: {
  nodes: JourneyNode[];
  connectors: JourneyConnectorVisual[];
}) {
  if (nodes.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500" fontSize="sm">
          No logistics path available for this order yet.
        </Text>
      </Box>
    );
  }

  return (
    <Flex w="100%" overflowX="auto" py={2} align="flex-start">
      {nodes.map((node, index) => (
        <JourneyNodeColumn
          key={node.id}
          node={node}
          connector={connectors[index]}
          isLast={index === nodes.length - 1}
        />
      ))}
    </Flex>
  );
}

export default function OrderTrackingModal({
  isOpen,
  onClose,
  order,
  shipment,
  isLoading = false,
}: Props) {
  const journey = buildOrderTrackingJourney(order, shipment);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent
        maxW="4xl"
        m={4}
        borderRadius="xl"
        overflow="hidden"
        maxH="90vh"
      >
        <Box
          bg="linear-gradient(135deg, #dbeafe 0%, #93c5fd 50%, #60a5fa 100%)"
          p={5}
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={3}>
              <Flex
                w={10}
                h={10}
                align="center"
                justify="center"
                bg="whiteAlpha.700"
                borderRadius="lg"
              >
                <Icon as={FaRoute} boxSize={5} color="blue.600" />
              </Flex>
              <VStack align="start" spacing={0}>
                <Heading size="md" color="gray.800">
                  Order Tracking
                </Heading>
                <Text fontSize="sm" color="gray.700">
                  Order # {journey.orderNo || order.orderNo || "—"}
                </Text>
              </VStack>
            </HStack>
            <IconButton
              aria-label="Close"
              icon={<FaTimes />}
              size="sm"
              variant="ghost"
              onClick={onClose}
            />
          </Flex>
        </Box>

        <Box p={6} overflowY="auto">
          <VStack spacing={5} align="stretch">
            <Flex
              align="flex-start"
              justify="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <VStack align="start" spacing={0}>
                <HStack spacing={2}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    Delivery Journey
                  </Text>
                  {isLoading && <Spinner size="xs" color="blue.500" />}
                </HStack>
                <Text fontSize="xs" color="gray.500">
                  Complete path of this order from source to final destination
                </Text>
              </VStack>
              <VStack align="end" spacing={0}>
                <Badge
                  colorScheme={badgeScheme(journey.overallBadge)}
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontSize="xs"
                >
                  {journey.overallBadge}
                </Badge>
                {journey.lastUpdatedLabel && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Last updated: {journey.lastUpdatedLabel}
                  </Text>
                )}
              </VStack>
            </Flex>

            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              p={5}
              bg="white"
            >
              <HorizontalJourney
                nodes={journey.nodes}
                connectors={journey.connectors}
              />
            </Box>

            <HStack
              spacing={3}
              align="center"
              bg="blue.50"
              borderWidth="1px"
              borderColor="blue.100"
              borderRadius="md"
              p={4}
            >
              <Flex
                w="36px"
                h="36px"
                minW="36px"
                borderRadius="full"
                bg="blue.100"
                align="center"
                justify="center"
              >
                <Icon as={FaTruck} color="blue.500" boxSize="16px" />
              </Flex>
              <VStack align="start" spacing={1} flex="1">
                <Text fontSize="sm" color="gray.700">
                  {journey.bannerText}
                </Text>
                {(journey.riderName || journey.riderPhone) && (
                  <Text fontSize="xs" color="gray.600">
                    Rider:{" "}
                    {[journey.riderName, journey.riderPhone]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                )}
                {journey.trackingUrl && (
                  <Text
                    as="a"
                    href={journey.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontSize="xs"
                    color="blue.600"
                    textDecoration="underline"
                  >
                    Open tracking link
                  </Text>
                )}
              </VStack>
            </HStack>
          </VStack>
        </Box>
      </ModalContent>
    </Modal>
  );
}
